"use client"

import { useEffect, useState } from "react"
import Link from "next/link"

const STORAGE_KEY = "fp51_register_promo"
const SHOW_DELAY_MS = 4500

/**
 * Lee una cookie por nombre (cliente).
 */
function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)")
  )
  return match ? decodeURIComponent(match[1]) : null
}

type Props = {
  /** País actual, p.ej. "es". Se usa para construir el link de cuenta. */
  countryCode?: string
}

const RegisterPromoPopup = ({ countryCode = "es" }: Props) => {
  const [open, setOpen] = useState(false)
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)

    // No mostrar si ya está logueado (Medusa guarda el JWT en _medusa_jwt)
    const isLoggedIn = !!getCookie("_medusa_jwt")
    if (isLoggedIn) return

    // No mostrar si ya se cerró antes (sin importar la razón)
    if (typeof window !== "undefined") {
      const stored = window.localStorage.getItem(STORAGE_KEY)
      if (stored) return
    }

    const t = window.setTimeout(() => setOpen(true), SHOW_DELAY_MS)
    return () => window.clearTimeout(t)
  }, [])

  const close = (reason: "dismiss" | "later" | "register") => {
    if (dontShowAgain || reason === "register") {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ dismissedAt: Date.now(), reason })
      )
    } else {
      // Si pulsa "Más tarde", guardamos igualmente pero por un periodo corto (24h)
      // para no atosigar al usuario. Si marca la casilla, se guarda para siempre.
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ dismissedAt: Date.now(), reason, until: Date.now() + 24 * 60 * 60 * 1000 })
      )
    }
    setOpen(false)
  }

  if (!mounted || !open) return null

  const accountHref = `/${countryCode}/account`

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="fp51-register-promo-title"
      className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-6 sm:px-6"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/55 backdrop-blur-sm transition-opacity duration-300 animate-fade-in"
        onClick={() => close("dismiss")}
        aria-hidden="true"
      />

      {/* Card */}
      <div
        className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden grid grid-cols-1 md:grid-cols-5 animate-[fp51-popup-in_450ms_cubic-bezier(.21,1.02,.73,1)_both]"
      >
        {/* Cerrar */}
        <button
          onClick={() => close("dismiss")}
          aria-label="Cerrar"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-gray-700 hover:text-pink-600 shadow flex items-center justify-center transition"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M6 6L18 18M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* Lado izquierdo decorativo */}
        <div className="relative md:col-span-2 bg-gradient-to-br from-pink-400 via-pink-500 to-rose-500 text-white p-8 flex flex-col justify-between overflow-hidden min-h-[220px]">
          {/* Cruz de farmacia decorativa */}
          <div
            aria-hidden="true"
            className="absolute -top-10 -right-10 w-48 h-48 bg-white/10 rounded-full"
          />
          <div
            aria-hidden="true"
            className="absolute -bottom-12 -left-12 w-56 h-56 bg-white/10 rounded-full"
          />
          <div
            aria-hidden="true"
            className="absolute top-1/2 right-6 -translate-y-1/2 opacity-25"
          >
            {/* Cruz grande tipo farmacia */}
            <svg width="140" height="140" viewBox="0 0 100 100" fill="white">
              <rect x="38" y="10" width="24" height="80" rx="4" />
              <rect x="10" y="38" width="80" height="24" rx="4" />
            </svg>
          </div>

          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-block w-2 h-2 rounded-full bg-white animate-pulse" />
              <span className="uppercase tracking-widest text-xs font-semibold text-white/90">
                Farmacia Paseo 51
              </span>
            </div>
            <h2
              id="fp51-register-promo-title"
              className="text-3xl sm:text-4xl font-bold leading-tight"
            >
              Regístrate y
              <br />
              ahorra más
            </h2>
            <p className="mt-3 text-white/95 text-sm sm:text-base">
              Crea tu cuenta gratis en menos de 1 minuto.
            </p>
          </div>

          <div className="relative z-10 mt-6 flex items-center gap-2 text-xs text-white/90">
            <span className="inline-flex w-7 h-7 rounded-full bg-white/20 items-center justify-center">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2L15 8.5L22 9.3L17 14.1L18.2 21L12 17.8L5.8 21L7 14.1L2 9.3L9 8.5L12 2Z"
                  fill="white"
                />
              </svg>
            </span>
            <span>
              Únete a más de <strong>1.000 clientes</strong> que ya ahorran.
            </span>
          </div>
        </div>

        {/* Lado derecho: beneficios */}
        <div className="md:col-span-3 p-7 sm:p-8 flex flex-col">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 leading-snug">
            Si te registras y aceptas recibir nuestras comunicaciones,
            <span className="text-pink-600"> recibirás ofertas y descuentos exclusivos.</span>
          </h3>

          <ul className="mt-5 space-y-3 text-sm sm:text-base text-gray-700">
            <li className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex w-6 h-6 rounded-full bg-pink-100 text-pink-600 items-center justify-center shrink-0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12.5L10 17.5L19 7.5"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span>
                <strong>Ofertas y descuentos exclusivos</strong> solo para
                suscriptores.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex w-6 h-6 rounded-full bg-pink-100 text-pink-600 items-center justify-center shrink-0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12.5L10 17.5L19 7.5"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span>
                <strong>Anticipo de novedades</strong> en parafarmacia y
                cosmética.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex w-6 h-6 rounded-full bg-pink-100 text-pink-600 items-center justify-center shrink-0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12.5L10 17.5L19 7.5"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span>
                <strong>Seguimiento de tus pedidos</strong> y guardado de tus
                datos de envío.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex w-6 h-6 rounded-full bg-pink-100 text-pink-600 items-center justify-center shrink-0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12.5L10 17.5L19 7.5"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span>
                <strong>Atención personalizada</strong> de nuestro equipo
                farmacéutico.
              </span>
            </li>
          </ul>

          <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:gap-3">
            <Link
              href={accountHref}
              onClick={() => close("register")}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-pink-600 hover:bg-pink-700 active:bg-pink-800 text-white font-semibold rounded-full px-5 py-3 text-sm sm:text-base shadow-md hover:shadow-lg transition"
            >
              Crear cuenta gratis
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12H19M19 12L13 6M19 12L13 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
            <button
              onClick={() => close("later")}
              className="inline-flex items-center justify-center text-gray-600 hover:text-gray-900 font-medium px-4 py-3 text-sm sm:text-base"
            >
              Más tarde
            </button>
          </div>

          <label className="mt-4 flex items-center gap-2 text-xs text-gray-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
            />
            <span>No volver a mostrar este mensaje.</span>
          </label>

          <p className="mt-4 text-[11px] leading-snug text-gray-400">
            Al registrarte aceptas recibir comunicaciones comerciales de
            Farmacia Paseo 51. Puedes darte de baja en cualquier momento desde
            tu cuenta o el pie de nuestros correos. Consulta nuestra{" "}
            <Link
              href="/informacion/politica-de-privacidad"
              className="underline hover:text-gray-600"
            >
              Política de Privacidad
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  )
}

export default RegisterPromoPopup